import { apiRequest } from "./apiClient";

export type BusinessCaseCategoryId = "brand-partnership" | "campaign-of-the-year" | "service-provider";

export type BusinessCaseRegistrationPayload = {
    companyName: string;
    vatId: string;
    streetAddress: string;
    postalCode: string;
    city: string;
    country: string;
    generalPhone: string;
    generalEmail: string;
    differentBillingAddress: boolean;
    billingCompanyName: string;
    billingStreetAddress: string;
    billingPostalCode: string;
    billingCity: string;
    billingCountry: string;
    contactFirstName: string;
    contactLastName: string;
    salutation: string;
    contactPhone: string;
    contactEmail: string;
    brandPartnershipCount: number;
    campaignCount: number;
    serviceProviderCount: number;
    netPricesAccepted: boolean;
    conditionsAccepted: boolean;
    legallyBindingOfferAccepted: boolean;
    cancellationAccepted: boolean;
    privacyAccepted: boolean;
};

export type BusinessCaseAttachmentPayload = {
    fileName: string;
    fileSize: number;
    fileExtension: string;
    mimeType: string | null;
};

export type BusinessCasePayload = {
    registrationId: string;
    category: BusinessCaseCategoryId;
    caseTitle: string;
    shortDescription: string;
    challenge: string;
    objectives: string;
    execution: string;
    impactMetrics: string;
    strategicValue: string;
    attachments: BusinessCaseAttachmentPayload[];
    files: File[];
};

export type BusinessCaseRegistrationResponse = {
    id: string;
};

export type BusinessCaseSubmissionResponse = {
    id: string;
    registrationId: string;
    category: BusinessCaseCategoryId;
    submissionNumber: string;
    caseTitle: string;
    createdAt: string;
};

export type BusinessCaseDraftState = {
    token?: string;
    currentStep: number;
    selectedCategoryId: BusinessCaseCategoryId;
    registrationData: Record<string, unknown>;
    caseData: Record<string, unknown>;
    uploadedFiles: Array<Record<string, unknown>>;
    submittedCases: Array<Record<string, unknown>>;
    backendRegistrationId: string | null;
    submissionId: string | null;
    submissionDate: string | null;
    draftCaseKey: string;
    createdAt?: string;
    updatedAt?: string;
};

export async function createBusinessCaseRegistration(payload: BusinessCaseRegistrationPayload) {
    return apiRequest<BusinessCaseRegistrationResponse>("/api/business-case-registrations", {
        method: "POST",
        body: payload,
    });
}

export async function createBusinessCase(payload: BusinessCasePayload, securityToken: string) {
    const formData = new FormData();

    formData.append("registrationId", payload.registrationId);
    formData.append("category", payload.category);
    formData.append("caseTitle", payload.caseTitle);
    formData.append("shortDescription", payload.shortDescription);
    formData.append("challenge", payload.challenge);
    formData.append("objectives", payload.objectives);
    formData.append("execution", payload.execution);
    formData.append("impactMetrics", payload.impactMetrics);
    formData.append("strategicValue", payload.strategicValue);
    formData.append("attachments", JSON.stringify(payload.attachments));

    payload.files.forEach((file) => {
        formData.append("files", file);
    });

    // Das Captcha-Token geht als Header raus, nicht als FormData-Feld:
    // so kann der Server es prüfen, bevor er die Dateien entgegennimmt.
    return apiRequest<BusinessCaseSubmissionResponse>("/api/business-cases", {
        method: "POST",
        body: formData,
        headers: { "x-turnstile-token": securityToken },
    });
}

export async function saveBusinessCaseDraft(payload: BusinessCaseDraftState) {
    return apiRequest<BusinessCaseDraftState>("/api/business-case-drafts", {
        method: "POST",
        body: payload,
    });
}

export async function getBusinessCaseDraft(token: string) {
    return apiRequest<BusinessCaseDraftState>(`/api/business-case-drafts/${token}`);
}

// ── Admin: Übersicht aller eingereichten Business Cases (geschützt) ───────
export type BusinessCaseOverviewItem = {
    id: string;
    submissionNumber: string;
    category: BusinessCaseCategoryId;
    caseTitle: string;
    status: string;
    createdAt: string;
    companyName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
};

export async function listBusinessCases(token: string): Promise<BusinessCaseOverviewItem[]> {
    return apiRequest<BusinessCaseOverviewItem[]>("/api/admin/business-cases", { token });
}
